import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer using a dialog element.
 * Uses the native dialog element's top layer to ensure the menu appears above all content.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLButtonElement} menuButton - The menu toggle button.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['dialog', 'menuButton'];

  #previousScrollY = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    // Close dialog when clicking on the backdrop
    this.refs.dialog.addEventListener('click', this.#onDialogClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.refs.dialog.removeEventListener('click', this.#onDialogClick);
  }

  /**
   * Handle clicks on the dialog - close if clicking the backdrop
   * @param {MouseEvent} event
   */
  #onDialogClick = (event) => {
    // If the click is directly on the dialog (not its children), it's on the backdrop
    if (event.target === this.refs.dialog) {
      this.close();
    }
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    // Check if we're in a submenu
    const openSubmenu = this.refs.dialog.querySelector('details.menu-open:not(.menu-drawer-container)');
    if (openSubmenu) {
      this.#closeSubmenu(openSubmenu);
    } else if (this.isOpen) {
      this.close();
    }
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.dialog.open;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the main menu drawer or a submenu
   * @param {Event} [event]
   */
  open(event) {
    // Check if this is a submenu open request
    const target = event?.target instanceof Element ? event.target : null;
    const submenuDetails = target?.closest('details:not(.menu-drawer-container)');

    if (submenuDetails && this.isOpen) {
      // Opening a submenu within the drawer
      this.#openSubmenu(submenuDetails);
      return;
    }

    // Opening the main drawer
    if (this.isOpen) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    this.refs.menuButton.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(this.refs.dialog);

    requestAnimationFrame(() => {
      // Lock scroll
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      // Show the dialog in the top layer
      this.refs.dialog.showModal();
      this.refs.dialog.classList.add('menu-open');

      // Wait for the drawer animation to complete before trapping focus
      const drawer = this.refs.dialog.querySelector('.menu-drawer');
      onAnimationEnd(drawer || this.refs.dialog, () => trapFocus(this.refs.dialog), { subtree: false });
    });
  }

  /**
   * Open a submenu within the drawer
   * @param {HTMLDetailsElement} details
   */
  #openSubmenu(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    details.setAttribute('open', '');

    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      const submenu = details.querySelector('.menu-drawer__submenu');
      if (submenu) {
        onAnimationEnd(submenu, () => trapFocus(details), { subtree: false });
      }
    });
  }

  /**
   * Go back from a submenu or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    const target = event?.target instanceof Element ? event.target : null;
    const submenuDetails = target?.closest('details.menu-open:not(.menu-drawer-container)');

    if (submenuDetails) {
      this.#closeSubmenu(submenuDetails);
    } else {
      this.close();
    }
  }

  /**
   * Close the main menu drawer
   */
  close() {
    if (!this.isOpen) return;

    this.refs.menuButton.setAttribute('aria-expanded', 'false');
    this.refs.dialog.classList.remove('menu-open');
    this.refs.dialog.classList.add('dialog-closing');

    const drawer = this.refs.dialog.querySelector('.menu-drawer');

    onAnimationEnd(
      drawer || this.refs.dialog,
      () => {
        // Restore scroll
        document.body.style.width = '';
        document.body.style.position = '';
        document.body.style.top = '';
        window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

        this.refs.dialog.close();
        this.refs.dialog.classList.remove('dialog-closing');
        removeTrapFocus();

        // Reset any open submenus
        const openDetails = this.refs.dialog.querySelectorAll('details[open]');
        openDetails.forEach((detail) => {
          detail.classList.remove('menu-open');
          detail.removeAttribute('open');
          detail.querySelector('summary')?.setAttribute('aria-expanded', 'false');
        });
      },
      { subtree: false }
    );
  }

  /**
   * Close a submenu
   * @param {HTMLDetailsElement} details
   */
  #closeSubmenu(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    const submenu = details.querySelector('.menu-drawer__submenu');

    onAnimationEnd(
      submenu || details,
      () => {
        details.removeAttribute('open');
        trapFocus(this.refs.dialog);
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLElement} container - The container element with accordions
   */
  preventInitialAccordionAnimations(container) {
    const content = container.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}
